import type { UserPreferences } from '@court-sync/shared';
import type { TourStep } from '@/components/ui/Tour';
import { DRAWER_PANEL_ENTER_DURATION_MS } from '@/components/ui/Drawer';
import { TOUR_CENTERED_STEP_SELECTOR } from '@/lib/tour-constants';
import { getPageTourDefinition, type PageTourKey } from './registry';
import { isPageTourComplete } from './pageTourCompletion';

const TARGET_POLL_MS = 50;
const TARGET_TIMEOUT_MS = 2500;
const DRAWER_ANIMATION_BUFFER_MS = 24;

function findTourTarget(selectorId: string): Element | null {
  return (
    document.getElementById(selectorId) ?? document.querySelector(`[data-tour="${selectorId}"]`)
  );
}

export function isTourStepSelectorReady(selectorId: string): boolean {
  if (!selectorId.trim() || selectorId === TOUR_CENTERED_STEP_SELECTOR) {
    return true;
  }

  return findTourTarget(selectorId) != null;
}

export function areTourStepTargetsReady(steps: TourStep[]): boolean {
  if (steps.length === 0) return false;
  return steps.every((step) => isTourStepSelectorReady(step.selectorId));
}

/** Only the first step must exist before starting — later steps mount on tab enter. */
export function areTourFirstStepTargetsReady(steps: TourStep[]): boolean {
  if (steps.length === 0) return false;
  return isTourStepSelectorReady(steps[0].selectorId);
}

export function arePageTourTargetsReady(tourKey: PageTourKey): boolean {
  const definition = getPageTourDefinition(tourKey);
  if (!definition) return false;

  if (definition.steps.length === 0) {
    return definition.deferToPage === true;
  }

  return areTourStepTargetsReady(definition.steps);
}

/** First step only — later steps may mount after tab switches (e.g. Starter payments tour). */
export function arePageTourFirstStepTargetsReady(tourKey: PageTourKey): boolean {
  const definition = getPageTourDefinition(tourKey);
  if (!definition) return false;

  if (definition.steps.length === 0) {
    return definition.deferToPage === true;
  }

  return areTourFirstStepTargetsReady(definition.steps);
}

function scheduleTourStepsStartInternal(
  startTour: () => void,
  steps: TourStep[],
  afterAnimationMs: number,
): () => void {
  let cancelled = false;
  let pollTimer: number | undefined;
  let startTimer: number | undefined;
  const pollStartedAt = Date.now();

  const attemptStart = () => {
    if (cancelled) return;
    if (areTourFirstStepTargetsReady(steps) || Date.now() - pollStartedAt >= TARGET_TIMEOUT_MS) {
      startTour();
      return;
    }
    pollTimer = window.setTimeout(attemptStart, TARGET_POLL_MS);
  };

  startTimer = window.setTimeout(attemptStart, afterAnimationMs);

  return () => {
    cancelled = true;
    if (startTimer !== undefined) window.clearTimeout(startTimer);
    if (pollTimer !== undefined) window.clearTimeout(pollTimer);
  };
}

function schedulePageTourStartInternal(
  startPageTour: (key: PageTourKey) => void,
  tourKey: PageTourKey,
  afterAnimationMs: number,
): () => void {
  let cancelled = false;
  let pollTimer: number | undefined;
  let startTimer: number | undefined;
  const pollStartedAt = Date.now();

  const attemptStart = () => {
    if (cancelled) return;
    if (
      arePageTourFirstStepTargetsReady(tourKey) ||
      Date.now() - pollStartedAt >= TARGET_TIMEOUT_MS
    ) {
      startPageTour(tourKey);
      return;
    }
    pollTimer = window.setTimeout(attemptStart, TARGET_POLL_MS);
  };

  startTimer = window.setTimeout(attemptStart, afterAnimationMs);

  return () => {
    cancelled = true;
    if (startTimer !== undefined) window.clearTimeout(startTimer);
    if (pollTimer !== undefined) window.clearTimeout(pollTimer);
  };
}

/** Wait for tour anchors (e.g. after a tab switch), then start the tour. */
export function schedulePageTourStart(
  startPageTour: (key: PageTourKey) => void,
  tourKey: PageTourKey,
  options?: { afterAnimationMs?: number },
): () => void {
  const afterAnimationMs = options?.afterAnimationMs ?? 0;
  return schedulePageTourStartInternal(startPageTour, tourKey, afterAnimationMs);
}

/** Wait for drawer enter animation + tour anchors, then start a first-time page tour. */
export function scheduleAutoPageTour(
  startPageTour: (key: PageTourKey) => void,
  tourKey: PageTourKey,
  preferences: UserPreferences | undefined,
  options?: { afterAnimationMs?: number },
): () => void {
  const definition = getPageTourDefinition(tourKey);
  if (!definition || isPageTourComplete(preferences, tourKey, definition.legacyPreferenceKey)) {
    return () => {};
  }

  const afterAnimationMs =
    options?.afterAnimationMs ?? DRAWER_PANEL_ENTER_DURATION_MS + DRAWER_ANIMATION_BUFFER_MS;
  return schedulePageTourStartInternal(startPageTour, tourKey, afterAnimationMs);
}

/** Wait for custom step anchors, then start a tour with explicit steps. */
export function scheduleTourStepsStart(
  startTour: () => void,
  steps: TourStep[],
  options?: { afterAnimationMs?: number },
): () => void {
  const afterAnimationMs = options?.afterAnimationMs ?? 0;
  return scheduleTourStepsStartInternal(startTour, steps, afterAnimationMs);
}
